import { Checkbox, RHF } from "@devhop/ui";
import { useEffect, useMemo } from "react";
import { config } from "@/shared/lib/config";
import { addMinutesToTimeHhMm, formatTime } from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
} from "@/shared/lib/private-lesson-slot-duration";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { RehearsalStudioConflictAlert } from "./RehearsalStudioConflictAlert";
import type { RehearsalStudioFormSlice } from "./rehearsal-session-form-types";
import { useRehearsalStudioConflict } from "./rehearsal-studio-conflict";

type RoomItem = { id: string; name: string };

type RehearsalStudioBookingFieldsProps = {
  canBookStudio: boolean;
  defaultRoomId?: string | null;
  defaultRoomName?: string | null;
  disabled?: boolean;
  variant?: "create" | "reschedule";
};

export function RehearsalStudioBookingFields({
  canBookStudio,
  defaultRoomId,
  defaultRoomName,
  disabled,
  variant = "create",
}: RehearsalStudioBookingFieldsProps) {
  const { Controller, useFormContext } = RHF;
  const { control, setValue, watch } =
    useFormContext<RehearsalStudioFormSlice & Record<string, unknown>>();
  const { conflictMessage, conflictDetails, isChecking } =
    useRehearsalStudioConflict();

  const bookStudio = watch("bookStudio");
  const studioRoomId = watch("studioRoomId");
  const scheduledDates = (watch("scheduledDates") as string[] | undefined) ?? [];
  const studioStartTime = watch("studioStartTime");
  const studioSlotMinutes = watch("studioSlotMinutes");

  const showStudioFields = !!bookStudio;

  const computedEndTime = useMemo(() => {
    if (!studioStartTime?.trim()) return "";
    if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(studioSlotMinutes)) return "";
    return addMinutesToTimeHhMm(studioStartTime, studioSlotMinutes);
  }, [studioStartTime, studioSlotMinutes]);

  const removeScheduledDate = (date: string) => {
    setValue(
      "scheduledDates",
      scheduledDates.filter((d) => d !== date),
      { shouldValidate: true },
    );
  };

  useEffect(() => {
    if (defaultRoomId && !studioRoomId) {
      setValue("studioRoomId", defaultRoomId, { shouldValidate: false });
    }
  }, [defaultRoomId, studioRoomId, setValue]);

  if (!canBookStudio) {
    return (
      <p className="rounded-lg border bg-muted/30 px-3 py-2.5 text-muted-foreground text-xs">
        ບໍ່ມີສິດຈອງຫ້ອງ — ບັນທຶກວັນຊ້ອມໄດ້ ແຕ່ບໍ່ສ້າງການຈອງ Studio ອັດຕະໂນມັດ
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
      {variant === "create" ? (
        <Controller
          control={control}
          name="bookStudio"
          render={({ field }) => {
            const checkboxId = "rehearsal-book-studio";
            return (
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id={checkboxId}
                  checked={!!field.value}
                  disabled={disabled}
                  onCheckedChange={(v) => field.onChange(!!v)}
                />
                <label htmlFor={checkboxId} className="cursor-pointer">
                  <span className="font-medium text-sm">
                    ຈອງຫ້ອງ Studio ພ້ອມກັນ
                  </span>
                  <span className="mt-0.5 block text-muted-foreground text-xs">
                    ສ້າງການຈອງຫ້ອງຕໍ່ວັນທີ່ເລືອກ — ເວລາເດີມທຸກວັນ
                    {defaultRoomName ? ` (ແນະນຳ: ${defaultRoomName})` : ""}
                  </span>
                </label>
              </div>
            );
          }}
        />
      ) : (
        <div>
          <p className="font-medium text-sm">ການຈອງຫ້ອງ Studio</p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            ປັບວັນ ເວລາ ຫຼື ຫ້ອງ — ລະບົບຈະອັບເດດການຈອງຫ້ອງພ້ອມກັນ
          </p>
        </div>
      )}

      {showStudioFields && (
        <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <FormInfiniteCombobox<RoomItem>
              label="ຫ້ອງ Studio"
              name="studioRoomId"
              placeholder="ເລືອກຫ້ອງ..."
              queryKey={["performance", "rooms", "rehearsal-booking"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<RoomItem>(
                  `${config.apiUrl}/master/rooms/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<RoomItem>(
                  `${config.apiUrl}/master/rooms/lookup`,
                  id,
                ).then((item) => item ?? null)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
          </div>

          <div className="space-y-2">
            <p className="font-medium text-sm leading-none">ເວລາເລີ່ມຕົ້ນ</p>
            <Controller
              control={control}
              name="studioStartTime"
              render={({ field, fieldState }) => (
                <>
                  <QuarterHourMinuteSelect
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    disabled={disabled}
                  />
                  {fieldState.error?.message ? (
                    <p className="text-red-500 text-xs">
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <p className="font-medium text-sm leading-none">ຄວາມຍາວ (ນາທີ)</p>
            <Controller
              control={control}
              name="studioSlotMinutes"
              render={({ field, fieldState }) => {
                const raw = Number(field.value);
                const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                  ? raw
                  : 60;
                return (
                  <>
                    <SimpleSelect
                      value={String(safe)}
                      onValueChange={(v) => {
                        const minutes = Number(v);
                        if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(minutes))
                          return;
                        field.onChange(minutes);
                      }}
                      options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                      disabled={disabled}
                      className="h-9 w-full"
                      placeholder="ເລືອກຄວາມຍາວ"
                    />
                    {fieldState.error?.message ? (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    ) : null}
                  </>
                );
              }}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <p className="font-medium text-muted-foreground text-sm leading-none">
              ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນອັດຕະໂນມັດ)</span>
            </p>
            <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
              {computedEndTime ? formatTime(computedEndTime) : "—"}
            </div>
          </div>

          {isChecking && !conflictMessage && (
            <p className="text-muted-foreground text-xs sm:col-span-2">
              ກຳລັງກວດສອບຫ້ອງວ່າງ...
            </p>
          )}

          {conflictMessage && (
            <RehearsalStudioConflictAlert
              message={conflictMessage}
              details={conflictDetails}
              studioRoomId={studioRoomId}
              disabled={disabled}
              showRemoveDate={variant === "create"}
              onRemoveDate={removeScheduledDate}
              onSelectRoom={(roomId) => {
                setValue("studioRoomId", roomId, { shouldValidate: true });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
